import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { VENDOR_DEFINITIONS } from '../../models/integration/vendor-definitions.constant';
import { IntegrationProvider } from '../../provider/integration.provider';
import { SetupStep } from '../../reducer/integration.reducer';

/**
 * Modal step shown to the user. Local view state derived from `pendingSetup.step`.
 *
 * Same three step shape as D365 BC's connect modal: welcome (form) → testing
 * (spinner while BE runs discovery + token + smoke test) → error. No `created`
 * step: on success the manage modal takes over and this modal closes.
 *
 * NOTE: the reducer uses `awaiting_consent` for both vendors — the same string,
 * different meaning. For D365 BC it means "Microsoft popup is open"; for Abacus
 * it means "BE is testing the credentials". We map both to the local
 * `'testing'` step here so the loading copy reads naturally for Abacus users.
 */
export type AbacusModalViewStep = 'welcome' | 'testing' | 'error';

@Component({
    selector: 'aumico-abacus-connect-modal',
    templateUrl: './abacus-connect-modal.component.html',
    styleUrls: ['./abacus-connect-modal.component.scss'],
})
export class AbacusConnectModalComponent implements OnInit, OnDestroy {
    @Output() public closed = new EventEmitter<void>();

    public step: AbacusModalViewStep = 'welcome';
    public errorMessage: string | null = null;

    /** Form values: local to the modal until submitted. NOT in NgRx until dispatch. */
    public displayName = '';
    public serverUrl = '';
    public clientId = '';
    public clientSecret = '';
    /** Toggles the password masking on the clientSecret field. */
    public showClientSecret = false;

    public readonly vendorDefinition = VENDOR_DEFINITIONS.find(
        (v) => v.vendor === IntegrationVendor.ABACUS,
    );

    private readonly destroy$ = new Subject<void>();

    public constructor(private readonly integrationProvider: IntegrationProvider) {}

    public ngOnInit(): void {
        this.integrationProvider
            .getPendingSetup$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((setup) => {
                this.errorMessage = setup.error;

                if (setup.vendor !== IntegrationVendor.ABACUS) return;

                // On success: the manage modal takes over. Close this immediately.
                if (setup.step === 'created') {
                    this.closed.emit();
                    return;
                }

                this.step = this.viewStepFor(setup.step);
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * All four fields required. Display name distinguishes connections in the
     * manage modal's server list — same rationale as D365 BC's mandatory
     * display name. Server URL must look like a URL (basic Regex), the rest
     * are non empty strings.
     */
    public get isFormValid(): boolean {
        const urlOk = /^https?:\/\/[^/\s]+$/i.test(this.serverUrl.trim());
        return (
            this.displayName.trim().length > 0 &&
            urlOk &&
            this.clientId.trim().length > 0 &&
            this.clientSecret.trim().length > 0
        );
    }

    public onSubmit(): void {
        if (!this.isFormValid) return;
        this.integrationProvider.startAbacusConnect({
            displayName: this.displayName.trim(),
            serverUrl: this.serverUrl.trim(),
            clientId: this.clientId.trim(),
            clientSecret: this.clientSecret.trim(),
        });
    }

    public onToggleClientSecret(): void {
        this.showClientSecret = !this.showClientSecret;
    }

    public onRetryFromError(): void {
        // Clear the reducer's pendingSetup slice FIRST: otherwise the next
        // `getPendingSetup$()` emission re-maps `step: 'error'` and yanks us
        // straight back to the error view. cancelSetup wipes the slice without
        // closing the modal.
        this.integrationProvider.cancelSetup();
        this.errorMessage = null;
        this.step = 'welcome';
    }

    public onCancel(): void {
        this.integrationProvider.cancelSetup();
        this.closed.emit();
    }

    private viewStepFor(reducerStep: SetupStep): AbacusModalViewStep {
        switch (reducerStep) {
            case 'awaiting_consent':
                return 'testing';
            case 'error':
                return 'error';
            case 'created':
                // Handled separately by closing the modal: should never render.
                return 'welcome';
            case 'idle':
            default:
                return 'welcome';
        }
    }
}
